import {differenceInDays, startOfDay, isPast} from 'date-fns';
import {
  dayOptions,
  timeOptions,
  defaultTime,
  amPmOptions,
  defaultAmPm,
  durationUnitOptions,
  defaultUnit,
} from './Settings/configUtils';

export const calculateTotals = (data, key) => {
  if (!Array.isArray(data)) return;
  return data?.reduce(
    (acc, item) => {
      const currentCount = parseInt(item[key], 10) || 0;
      acc[key] += currentCount || 0;
      return acc;
    },
    {[key]: 0},
  );
};

const SEVERITY_COLORS = new Map([
  ['INFO', '#4F46E5'],
  ['DEBUG', '#9CA3AF'],
  ['WARN', '#FBBF24'],
  ['ERROR', '#DC2626'],

  ['CRITICAL', '#7F1D1D'],
  ['HIGH', '#F87171'],
  ['MEDIUM', '#F97316'],
  ['LOW', '#2E7D32'],
  ['SUCCESS', '#059669'],
  ['UNKNOWN', '#374151'],
]);

const SEVERITY_TINTS = new Map([
  ['INFO', '#EEF2FF'],
  ['DEBUG', '#F9FAFB'],
  ['WARN', '#FFFBEB'],
  ['ERROR', '#FEF2F2'],

  ['CRITICAL', '#FEE2E2'],
  ['HIGH', '#FEF2F2'],
  ['MEDIUM', '#FFF7ED'],
  ['LOW', '#f1faf3'],
  ['SUCCESS', '#ECFDF5'],
  ['UNKNOWN', '#F3F4F6'],
]);

export function getSeverityColor(severityText, variant = 'solid') {
  if (!severityText) {
    const fallbackMap = variant === 'tint' ? SEVERITY_TINTS : SEVERITY_COLORS;
    return fallbackMap.get('UNKNOWN');
  }
  const normalizedText = String(severityText).trim().toUpperCase();
  const colorMap = variant === 'tint' ? SEVERITY_TINTS : SEVERITY_COLORS;

  const fallbackColor =
    variant === 'tint'
      ? SEVERITY_TINTS.get('UNKNOWN')
      : SEVERITY_COLORS.get('UNKNOWN');
  return colorMap.get(normalizedText) || fallbackColor;
}

export function generateSeverityColors(severitiesArray) {
  if (!Array.isArray(severitiesArray)) {
    console.error('Input must be an array.');
    return [];
  }

  const results = [];

  for (const text of severitiesArray) {
    const color = getSeverityColor(text, 'solid');
    results.push(color);
  }

  return results;
}

export function calculateSubscriptionStatus(expirationDateInput) {
  const expirationDate = new Date(expirationDateInput);

  if (isNaN(expirationDate.getTime())) {
    return {
      daysRemaining: NaN,
      expirationStatus: 'Invalid date provided.',
      isExpired: true,
    };
  }

  const todayStart = startOfDay(new Date());
  const expirationDayStart = startOfDay(expirationDate);

  const daysRemaining = differenceInDays(expirationDayStart, todayStart);

  let expirationStatus;
  const isExpired = isPast(expirationDayStart) && daysRemaining < 0;

  if (isExpired) {
    const daysAgo = Math.abs(daysRemaining);

    if (daysAgo === 1) {
      expirationStatus = 'Expired yesterday';
    } else {
      expirationStatus = `Expired ${daysAgo} days ago`;
    }
  } else if (daysRemaining === 0) {
    expirationStatus = 'Expires today';
  } else if (daysRemaining === 1) {
    expirationStatus = 'Expires tomorrow';
  } else {
    expirationStatus = `${daysRemaining} days remaining`;
  }

  return {
    daysRemaining,
    expirationStatus,
    isExpired,
  };
}

// Helper function to convert 24-hour time to 12-hour format
export const convert24To12Hour = time24 => {
  if (!time24) return {time: defaultTime, amPm: defaultAmPm};
  const [hours, minutes] = time24.split(':').map(Number);
  const hour12 =
    hours === 0 ? 12 : hours > 12 ? hours - 12 : hours === 12 ? 12 : hours;
  const amPm = hours >= 12 ? 'PM' : 'AM';
  const timeValue = `${hour12}:${minutes.toString().padStart(2, '0')}`;
  const timeOption =
    timeOptions.find(opt => opt.value === timeValue) || defaultTime;
  const amPmOption = amPmOptions.find(opt => opt.value === amPm) || defaultAmPm;
  return {time: timeOption, amPm: amPmOption};
};

// Helper function to convert 12-hour time to 24-hour format
export const convert12To24Hour = (timeOption, amPmOption) => {
  if (!timeOption || !amPmOption) return '00:00';
  const [hour12] = timeOption.value.split(':').map(Number);
  let hour24 = hour12;
  if (amPmOption.value === 'PM' && hour12 !== 12) {
    hour24 = hour12 + 12;
  } else if (amPmOption.value === 'AM' && hour12 === 12) {
    hour24 = 0;
  }
  return `${hour24.toString().padStart(2, '0')}:00`;
};

// Helper function to convert day name to lowercase for form
export const getDayOption = dayName => {
  if (!dayName) return dayOptions[0];
  const dayLower = dayName.toLowerCase();
  return dayOptions.find(opt => opt.value === dayLower) || dayOptions[0];
};

// Helper function to convert seconds to form value and unit
export const convertSecondsToForm = seconds => {
  if (!seconds) return {value: 0, unit: defaultUnit};
  if (seconds < 3600) {
    return {value: Math.floor(seconds / 60), unit: durationUnitOptions[0]}; // minutes
  } else if (seconds < 86400) {
    return {value: Math.floor(seconds / 3600), unit: durationUnitOptions[1]}; // hours
  } else {
    return {value: Math.floor(seconds / 86400), unit: durationUnitOptions[2]}; // days
  }
};

// Helper function to convert form value and unit to seconds
export const convertFormToSeconds = (value, unit) => {
  if (!value || !unit) return 0;
  const numValue = Number(value);
  switch (unit.value) {
    case 'minutes':
      return numValue * 60;
    case 'hours':
      return numValue * 3600;
    case 'days':
      return numValue * 86400;
    default:
      return 0;
  }
};

// Helper function to format duration breakdown directly from seconds
// Returns format like "1 day, 12 hours" or "1 hour, 35 minutes"
export const formatDurationBreakdownFromSeconds = seconds => {
  if (!seconds || seconds === 0) {
    return null;
  }

  // Calculate breakdown
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  // Build the breakdown string
  const parts = [];

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  }

  // If no parts (shouldn't happen, but safety check)
  if (parts.length === 0) {
    return null;
  }

  // Join parts with commas
  if (parts.length === 1) {
    return parts[0];
  } else if (parts.length === 2) {
    return parts.join(', ');
  } else {
    // For 3 parts: "1 day, 12 hours, 30 minutes"
    return parts.join(', ');
  }
};

// Helper function to format duration in human-readable breakdown from form value and unit
// This is used for real-time display as user types
export const formatDurationBreakdown = (value, unit) => {
  if (!value || !unit || Number(value) === 0) {
    return null;
  }

  // Convert to total seconds first
  const totalSeconds = convertFormToSeconds(value, unit);

  return formatDurationBreakdownFromSeconds(totalSeconds);
};
